<template>
  <div>
    <Navbar />
    <component :is="componentName" />
  </div>
</template>

<script setup>
import Navbar from "~/components/Navbar.vue";
import RotatingCube from "~/components/home/skeleton/HeroIntroSectionSkeleton.vue";
const config = useRuntimeConfig();

//layout
definePageMeta({
  layout: "custom",
});
useHead({
  link: [
    {
      rel: "canonical",
      href: config.public.NUXT_BASE_URL,
    },
  ],
  script: [
    {
      src: "https://assets.calendly.com/assets/external/widget.js",
      async: true,
    },
    // Schema 1: Organization — anchors Travvir as a known entity in AI knowledge graphs
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://travvir.com/#organization",
        name: "Travvir",
        legalName: "Travvir",
        url: "https://travvir.com",
        logo: {
          "@type": "ImageObject",
          url: config.public.NUXT_CDN_URL + "/static/images/travvir-logo.png",
        },
        description:
          "Travvir is a smartphone-first AI virtual tour platform. Users create professional 3D virtual tours using only an iOS or Android smartphone — no 360-degree camera or hardware required.",
        foundingDate: "2019",
        founders: [{ "@type": "Person", name: "Rohan Mittal" }],
        areaServed: "Worldwide",
        knowsAbout: [
          "Virtual Tour",
          "360 Photography",
          "Photogrammetry",
          "Real Estate Photography",
          "Spatial AI",
          "Augmented Reality",
          "Immersive Technology",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "operations@travvir.com",
          telephone: "+44 7435 902658",
        },
        sameAs: [
          "https://www.linkedin.com/company/travvir/",
          "https://x.com/Travvir_AI",
          "https://www.crunchbase.com/organization/travvir",
        ],
      }),
    },
    // Schema 2: WebSite — enables Google/AI Sitelinks Search Box and confirms canonical identity
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://travvir.com/#website",
        name: "Travvir",
        url: "https://travvir.com",
        publisher: { "@id": "https://travvir.com/#organization" },
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "https://travvir.com/explore?q={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      }),
    },
    // Schema 3: SoftwareApplication — tells AI models this is a product with specific capabilities
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": "https://travvir.com/#app",
        name: "Travvir Virtual Tour Creator",
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "Virtual Tour Software",
        operatingSystem: "iOS, Android",
        url: "https://travvir.com",
        description:
          "Create immersive 3D virtual tours using only a smartphone. No 360-degree camera required. AI-powered stitching, cloud hosting, embed, and VR viewing included.",
        featureList: [
          "Smartphone-based 360° capture",
          "AI-powered image stitching",
          "Cloud hosting",
          "Iframe embed",
          "Interactive waypoints",
          "VR mode",
          "Analytics",
          "Google Maps integration",
          "Multi-language support",
        ],
        screenshot:
          config.public.NUXT_CDN_URL + "/static/images/homepage-background.png",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          description: "Free to start — no hardware purchase required.",
        },
        author: { "@id": "https://travvir.com/#organization" },
        publisher: { "@id": "https://travvir.com/#organization" },
        isAccessibleForFree: true,
        availableOnDevice: ["iPhone", "Android Phone"],
      }),
    },
  ],
});

const componentName = shallowRef(RotatingCube);

if (import.meta.client) {
  componentName.value = defineAsyncComponent({
    loader: () => import("~/components/home/index.vue"),
    loadingComponent: RotatingCube,
    delay: 0,
  });
}
</script>

<style lang="scss" scoped></style>
